import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Badge, Button, Divider, Dropdown, Field, MessageBar, Option, Spinner, Tab, TabList,
  Textarea, Title3,
} from "@fluentui/react-components";
import {
  useAddComment, useAgents, useAssign, useAttachments, useChangeStatus, useComments, useMe,
  useRate, useTicket, useUpdateTicket, useUploadAttachment,
} from "../../api/hooks";
import { api } from "../../api/client";
import { isClosed } from "../../components/TicketTable";
import type { Priority, TicketStatus } from "../../types/models";

const PRIORITIES: Priority[] = ["Low", "Medium", "High", "Critical"];

const ALLOWED_TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  New: ["Open", "Assigned", "InProgress", "Escalated", "Closed"],
  Open: ["Assigned", "InProgress", "WaitingForUser", "Escalated", "Resolved", "Closed"],
  Assigned: ["InProgress", "WaitingForUser", "Escalated", "Resolved", "Closed"],
  InProgress: ["WaitingForUser", "Escalated", "Resolved", "Closed"],
  WaitingForUser: ["InProgress", "Resolved", "Closed"],
  Escalated: ["InProgress", "Resolved", "Closed"],
  Resolved: ["Closed", "Reopened"],
  Closed: ["Reopened"],
  Reopened: ["Open", "Assigned", "InProgress"],
};

export function TicketDetailPage() {
  const { id } = useParams();
  const me = useMe();
  const t = useTicket(id);
  const comments = useComments(id);
  const attachments = useAttachments(id);
  const addPublic = useAddComment(id, false);
  const addInternal = useAddComment(id, true);
  const changeStatus = useChangeStatus(id);
  const updateTicket = useUpdateTicket(id);
  const upload = useUploadAttachment(id);
  const rate = useRate(id);
  const assign = useAssign(id);
  const agents = useAgents();

  const [tab, setTab] = useState<"comments" | "attachments" | "details">("comments");
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [score, setScore] = useState(5);
  const [assigneeChoice, setAssigneeChoice] = useState<string>("");
  const [priorityChoice, setPriorityChoice] = useState<Priority | "">("");
  const [statusChoice, setStatusChoice] = useState<TicketStatus | "">("");
  const [statusNote, setStatusNote] = useState<string>("");

  if (t.isLoading || me.isLoading) return <Spinner />;
  if (t.isError) return <MessageBar intent="error">{(t.error as Error).message}</MessageBar>;
  const ticket = t.data!;
  const role = me.data!.user.role;
  const isAgentLike = role === "Agent" || role === "Admin";
  const isRequester = me.data!.user.id === ticket.requesterId;
  const closed = isClosed(ticket.status);
  // Agents/Admins can close any non-closed ticket. End users cannot close.
  const canClose = isAgentLike && !closed;
  // Agents/Admins can reopen any closed ticket; end users can reopen their own.
  const canReopen = closed && (isAgentLike || isRequester);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Title3>IT-{ticket.ticketNumber}: {ticket.title}</Title3>
          <Badge appearance="filled" color={closed ? "success" : "informative"}>
            {closed ? "Closed" : "Open"}
          </Badge>
          <Badge appearance="tint" color="brand">{ticket.priority}</Badge>
        </div>
        <p style={{ whiteSpace: "pre-wrap" }}>{ticket.description}</p>
        <Divider />
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as typeof tab)}>
          <Tab value="comments">Conversation</Tab>
          <Tab value="attachments">Attachments ({attachments.data?.length ?? 0})</Tab>
          <Tab value="details">Details</Tab>
        </TabList>

        {tab === "comments" && (
          <div style={{ marginTop: 12 }}>
            {(comments.data ?? []).map((c) => (
              <div key={c.id} style={{
                padding: 12, marginBottom: 8, borderRadius: 4,
                background: c.isInternal ? "rgba(255,200,0,0.12)" : "rgba(98,100,167,0.06)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>{c.author?.displayName}</strong>
                  <small>{new Date(c.createdAt).toLocaleString()}</small>
                </div>
                {c.isInternal && <Badge size="small" color="warning" appearance="tint">Internal</Badge>}
                <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{c.body}</div>
              </div>
            ))}
            <Divider />
            <Field label="Reply">
              <Textarea rows={3} value={reply} onChange={(_, d) => setReply(d.value)} />
            </Field>
            <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
              <Button appearance="primary" disabled={!reply.trim() || addPublic.isPending || addInternal.isPending}
                onClick={async () => {
                  if (internal) await addInternal.mutateAsync(reply);
                  else await addPublic.mutateAsync(reply);
                  setReply("");
                }}>
                {internal ? "Post internal note" : "Send reply"}
              </Button>
              {isAgentLike && (
                <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
                  Internal only
                </label>
              )}
            </div>
          </div>
        )}

        {tab === "attachments" && (
          <div style={{ marginTop: 12 }}>
            <input type="file" onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) await upload.mutateAsync(f);
              e.target.value = "";
            }} />
            <ul>
              {(attachments.data ?? []).map((a) => (
                <li key={a.id}>
                  <Button
                    appearance="transparent"
                    onClick={() => api.download(`/api/attachments/${a.id}/download`, a.fileName)}
                  >
                    {a.fileName}
                  </Button>{" "}({Math.round(a.sizeBytes / 1024)} KB)
                </li>
              ))}
            </ul>
          </div>
        )}

        {tab === "details" && (
          <dl style={{ marginTop: 12 }}>
            <dt>Category</dt><dd>{ticket.category?.name}</dd>
            <dt>Requester</dt><dd>{ticket.requester?.displayName}</dd>
            <dt>Assignee</dt><dd>{ticket.assignee?.displayName ?? "—"}</dd>
            <dt>Queue</dt><dd>{ticket.queue?.name ?? "—"}</dd>
            <dt>SLA due</dt><dd>{ticket.slaDueAt ? new Date(ticket.slaDueAt).toLocaleString() : "—"}</dd>
            <dt>Created</dt><dd>{new Date(ticket.createdAt).toLocaleString()}</dd>
          </dl>
        )}
      </div>

      <aside>
        <h4>Actions</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {canClose && (
            <Button
              appearance="primary"
              disabled={changeStatus.isPending}
              onClick={() => changeStatus.mutate({ status: "Closed" })}
            >
              Close ticket
            </Button>
          )}
          {canReopen && (
            <Button
              disabled={changeStatus.isPending}
              onClick={() => changeStatus.mutate({ status: "Reopened" })}
            >
              Reopen ticket
            </Button>
          )}
          {!canClose && !canReopen && (
            <div style={{ fontSize: 12, color: "#605E5C" }}>No status actions available.</div>
          )}
          {changeStatus.isError && (
            <MessageBar intent="error">{(changeStatus.error as Error).message}</MessageBar>
          )}
        </div>

        {isAgentLike && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ marginBottom: 4 }}>Assign agent</h4>
            <div style={{ fontSize: 12, color: "#605E5C", marginBottom: 6 }}>
              Currently: <strong>{ticket.assignee?.displayName ?? "Unassigned"}</strong>
            </div>
            <Dropdown
              placeholder="Pick an agent…"
              value={agents.data?.find((a) => a.id === assigneeChoice)?.displayName ?? ""}
              selectedOptions={assigneeChoice ? [assigneeChoice] : []}
              onOptionSelect={(_, d) => setAssigneeChoice((d.optionValue as string) ?? "")}
            >
              {(agents.data ?? []).map((a) => (
                <Option key={a.id} value={a.id} text={a.displayName}>
                  {a.displayName} · {a.role}
                </Option>
              ))}
            </Dropdown>
            <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              <Button
                size="small"
                appearance="primary"
                disabled={!assigneeChoice || assign.isPending}
                onClick={() => assign.mutate({ assigneeId: assigneeChoice })}
              >
                Assign
              </Button>
              <Button
                size="small"
                disabled={assign.isPending || ticket.assigneeId === me.data!.user.id}
                onClick={() => assign.mutate({ assigneeId: me.data!.user.id })}
              >
                Assign to me
              </Button>
            </div>
            {assign.isError && (
              <MessageBar intent="error" style={{ marginTop: 6 }}>
                {(assign.error as Error).message}
              </MessageBar>
            )}
          </div>
        )}

        {isAgentLike && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ marginBottom: 4 }}>Priority</h4>
            <div style={{ fontSize: 12, color: "#605E5C", marginBottom: 6 }}>
              Currently: <strong>{ticket.priority}</strong>
            </div>
            <Dropdown
              placeholder="Change priority…"
              value={priorityChoice}
              selectedOptions={priorityChoice ? [priorityChoice] : []}
              onOptionSelect={(_, d) => setPriorityChoice((d.optionValue as Priority) ?? "")}
            >
              {PRIORITIES.map((p) => (
                <Option key={p} value={p} text={p} disabled={p === ticket.priority}>
                  {p}
                </Option>
              ))}
            </Dropdown>
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <Button
                size="small"
                appearance="primary"
                disabled={!priorityChoice || priorityChoice === ticket.priority || updateTicket.isPending}
                onClick={() =>
                  updateTicket.mutate(
                    { priority: priorityChoice as Priority },
                    { onSuccess: () => setPriorityChoice("") },
                  )
                }
              >
                Update priority
              </Button>
            </div>
            {updateTicket.isError && (
              <MessageBar intent="error" style={{ marginTop: 6 }}>
                {(updateTicket.error as Error).message}
              </MessageBar>
            )}
          </div>
        )}

        {isAgentLike && (ALLOWED_TRANSITIONS[ticket.status]?.length ?? 0) > 0 && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ marginBottom: 4 }}>Status</h4>
            <div style={{ fontSize: 12, color: "#605E5C", marginBottom: 6 }}>
              Currently: <strong>{ticket.status}</strong>
            </div>
            <Dropdown
              placeholder="Change status…"
              value={statusChoice}
              selectedOptions={statusChoice ? [statusChoice] : []}
              onOptionSelect={(_, d) => setStatusChoice((d.optionValue as TicketStatus) ?? "")}
            >
              {(ALLOWED_TRANSITIONS[ticket.status] ?? []).map((s) => (
                <Option key={s} value={s} text={s}>
                  {s}
                </Option>
              ))}
            </Dropdown>
            <Field label="Note (optional)" style={{ marginTop: 6 }}>
              <Textarea
                rows={2}
                value={statusNote}
                onChange={(_, d) => setStatusNote(d.value)}
              />
            </Field>
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <Button
                size="small"
                appearance="primary"
                disabled={!statusChoice || changeStatus.isPending}
                onClick={() =>
                  changeStatus.mutate(
                    { status: statusChoice as TicketStatus, note: statusNote || undefined },
                    {
                      onSuccess: () => {
                        setStatusChoice("");
                        setStatusNote("");
                      },
                    },
                  )
                }
              >
                Update status
              </Button>
            </div>
          </div>
        )}

        {isRequester && (ticket.status === "Resolved" || ticket.status === "Closed") && (
          <div style={{ marginTop: 16 }}>
            <h4>Rate this resolution</h4>
            <input type="number" min={1} max={5} value={score} onChange={(e) => setScore(Number(e.target.value))} />
            <Button onClick={() => rate.mutate({ score })}>Submit rating</Button>
          </div>
        )}
      </aside>
    </div>
  );
}
